// ============================================================
// Task-assignment view logic (PURE — no React, no fetch)
// ============================================================
// Shared by the assignee picker + notify selector (Price Intelligence, Alert
// Feed, Action Queue create flows) and by the Action Queue's presentation of who
// owns a task and whether they were notified. Kept dependency-free so it is
// unit-testable with `node --test src/lib/taskAssign.test.ts`.
//
// HONESTY: the labels here mirror the server's real terminal statuses
// (task_notifications.status) — we never invent a "delivered" that didn't happen,
// and we never surface a teammate's email or phone.

export type NotifyOption = 'none' | 'email' | 'push' | 'both'

export interface NotifyChoice {
  value: NotifyOption
  label: string
  hint: string
}

// The four notify choices, in the order the selector renders them.
export const NOTIFY_CHOICES: readonly NotifyChoice[] = [
  { value: 'none', label: "Don't notify", hint: 'Just add the task to their queue' },
  { value: 'email', label: 'Email', hint: 'Send an email now' },
  { value: 'push', label: 'Push', hint: 'Send a phone or browser notification now' },
  { value: 'both', label: 'Email + push', hint: 'Send both' },
] as const

// An eligible assignee as returned by GET /api/v1/annotations/eligible-assignees
// (never carries a phone number).
export interface EligibleAssignee {
  user_id: string
  name: string
  email: string | null
  all_locations: boolean
}

// One per-channel delivery outcome as returned by POST /api/v1/annotations and by
// the enriched GET list (channel + status only).
export type DeliveryStatus = 'sent' | 'skipped_no_contact' | 'skipped_sms_disabled' | 'failed'
export interface DeliveryOutcome {
  channel: 'email' | 'sms' | 'push'
  status: DeliveryStatus | string
}

export function channelLabel(channel: string): string {
  return channel === 'push' ? 'Push' : channel === 'sms' ? 'Text' : 'Email'
}

export function deliveryStatusLabel(status: string): string {
  switch (status) {
    case 'sent': return 'sent'
    case 'failed': return 'failed'
    case 'skipped_no_contact': return 'no contact on file'
    case 'skipped_sms_disabled': return 'texting not enabled'
    default: return status.replace(/_/g, ' ')
  }
}

export type DeliveryTone = 'ok' | 'warn' | 'bad'
export function deliveryTone(status: string): DeliveryTone {
  if (status === 'sent') return 'ok'
  if (status === 'failed') return 'bad'
  return 'warn' // skipped_* — recorded honestly, but nothing was sent
}

/**
 * A compact, human phrase summarising per-channel outcomes for a task chip, e.g.
 *   [email:sent]                     → "Email sent"
 *   [email:sent, sms:failed]         → "Email sent · Text failed"
 *   []                               → null (nothing was requested)
 */
export function summarizeDeliveries(deliveries: DeliveryOutcome[] | undefined | null): string | null {
  if (!deliveries || !deliveries.length) return null
  return deliveries
    .map((d) => `${channelLabel(d.channel)} ${deliveryStatusLabel(d.status)}`)
    .join(' · ')
}

/** Worst tone across a set of outcomes — drives the chip colour. */
export function overallDeliveryTone(deliveries: DeliveryOutcome[] | undefined | null): DeliveryTone | null {
  if (!deliveries || !deliveries.length) return null
  if (deliveries.some((d) => deliveryTone(d.status) === 'bad')) return 'bad'
  if (deliveries.some((d) => deliveryTone(d.status) === 'warn')) return 'warn'
  return 'ok'
}

/**
 * Display label for a task's assignee_id. A Clerk user id resolves to the
 * member's name via the roster map; an unresolved user id degrades to a neutral
 * "Assigned teammate" (never the raw user_… id); a legacy free-text value is
 * shown verbatim. Returns null when unassigned.
 */
export function assigneeLabel(
  assigneeId: string | null | undefined,
  namesByUserId: Map<string, string>,
): string | null {
  const id = (assigneeId ?? '').trim()
  if (!id) return null
  const name = namesByUserId.get(id)
  if (name) return name
  if (id.startsWith('user_')) return 'Assigned teammate'
  return id // legacy free-text assignee
}

/**
 * Validate the assign dialog before submit. A notify request needs a selected
 * member; 'none' may be submitted with or without a member. Returns an error
 * string, or null when the form is valid.
 */
export function validateAssignForm(input: {
  notify: NotifyOption
  assigneeUserId: string | null
}): string | null {
  if (input.notify !== 'none' && !input.assigneeUserId) {
    return 'Choose a teammate to notify.'
  }
  return null
}

/** Build the id→name map used to resolve assignee_id on the Action Queue. */
export function namesFromAssignees(assignees: EligibleAssignee[]): Map<string, string> {
  const m = new Map<string, string>()
  for (const a of assignees) m.set(a.user_id, a.name)
  return m
}
